import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Loader2, RefreshCw, Search, ShieldCheck, ShieldX } from "lucide-react";
import BodyDetectionConfigPanel from "@/components/admin/BodyDetectionConfigPanel";
import AVSDetectionHealthBanner from "@/components/admin/AVSDetectionHealthBanner";

/**
 * BodyDetectionSection (Iter77e).
 * Admin-Übersicht + Manual-Override für die automatische Body-Content-Detection.
 */
export default function BodyDetectionSection() {
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [rescanning, setRescanning] = useState(false);
  const [error, setError] = useState("");
  const [rescanResult, setRescanResult] = useState(null);

  const loadStats = async () => {
    try {
      const r = await api.get("/admin/media/body-detection/stats");
      setStats(r.data);
    } catch (e) { setError(e?.response?.data?.detail || "Stats-Fehler"); }
  };
  const loadItems = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/media/body-detection/listings", { params: { filter_kind: filter, limit: 40 } });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) { setError(e?.response?.data?.detail || "Load-Fehler"); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadStats(); loadItems(); }, [filter]);

  const rescanAll = async () => {
    if (!window.confirm("Alle STX-Bilder neu scannen? Kann einige Minuten dauern.")) return;
    setRescanning(true); setError(""); setRescanResult(null);
    try {
      const r = await api.post("/admin/media/body-detection/rescan", null, { params: { force: false }, timeout: 300000 });
      setRescanResult(r.data);
      await loadStats(); await loadItems();
    } catch (e) { setError(e?.response?.data?.detail || "Rescan-Fehler"); }
    finally { setRescanning(false); }
  };

  const rescanOne = async (listingId) => {
    try {
      const r = await api.post("/admin/media/body-detection/rescan", null, { params: { listing_id: listingId, force: true }, timeout: 60000 });
      setRescanResult({ ...r.data, listing_id: listingId });
      await loadItems();
    } catch (e) { setError(e?.response?.data?.detail || "Rescan-Fehler"); }
  };

  const toggleUrl = async (listingId, url, currentlyBody) => {
    try {
      await api.post("/admin/media/body-detection/override", { listing_id: listingId, url, is_body: !currentlyBody });
      await loadItems(); await loadStats();
    } catch (e) { setError(e?.response?.data?.detail || "Override-Fehler"); }
  };

  return (
    <div data-testid="body-detection-section" className="space-y-4">
      {/* iter173b · AVS-Detection Livestatus-Banner (Alarm bei Detection-Ausfall) */}
      <AVSDetectionHealthBanner />

      {/* iter110c · Region-Config + Live Preview */}
      <BodyDetectionConfigPanel />

      <div className="flex items-baseline justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold">Body-Content-Detection · Übersicht</h2>
          <p className="text-xs text-[#666]">Automatische Klassifikation von STX-Artikelbildern (Körper-Inhalt → AVS-Blur).</p>
        </div>
        <button data-testid="body-detection-rescan-all" onClick={rescanAll} disabled={rescanning}
          className="h-9 px-3 bg-[#0A0A0A] text-white text-xs font-medium flex items-center gap-1.5 disabled:opacity-40">
          {rescanning ? <><Loader2 size={13} className="animate-spin" /> Scanne …</> : <><RefreshCw size={13} /> Alle STX rescannen</>}
        </button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="body-detection-stats">
          <div className="border border-[#E5E5E5] p-3">
            <p className="text-[10px] uppercase text-[#999]">STX-Listings</p>
            <p className="text-2xl font-bold">{stats.total_stx_listings}</p>
          </div>
          <div className="border border-[#E5E5E5] p-3">
            <p className="text-[10px] uppercase text-[#999]">Mit Media</p>
            <p className="text-2xl font-bold">{stats.listings_with_media}</p>
          </div>
          <div className="border border-emerald-400 bg-emerald-50 p-3">
            <p className="text-[10px] uppercase text-emerald-700">Mit Body-Content</p>
            <p className="text-2xl font-bold text-emerald-800">{stats.listings_with_body}</p>
          </div>
          <div className="border border-[#E5E5E5] p-3">
            <p className="text-[10px] uppercase text-[#999]">Body / Total Media</p>
            <p className="text-2xl font-bold">{stats.body_content_total} <span className="text-sm text-[#999]">/ {stats.media_total}</span></p>
            <p className="text-[10px] text-[#666]">{Math.round((stats.body_content_ratio || 0) * 100)}%</p>
          </div>
        </div>
      )}

      {rescanResult && (
        <div className="border-l-2 border-emerald-500 bg-emerald-50 p-2 text-xs text-emerald-800" data-testid="body-detection-rescan-result">
          Rescan fertig: {rescanResult.scanned} gescannt, {rescanResult.tagged} neu getaggt, {rescanResult.updated_listings} Listings aktualisiert
          {rescanResult.errors?.length > 0 && <>. Fehler: {rescanResult.errors.length}</>}.
        </div>
      )}
      {error && <div className="border border-red-400 bg-red-50 text-red-700 text-xs p-2">{error}</div>}

      <div className="flex gap-1 border-b border-[#E5E5E5]">
        {[
          { k: "all", l: "Alle" },
          { k: "body", l: "Nur Body-Content" },
          { k: "clean", l: "Unklassifiziert / Clean" },
        ].map((t) => (
          <button key={t.k} data-testid={`body-detection-filter-${t.k}`} onClick={() => setFilter(t.k)}
            className={`px-3 h-8 text-xs ${filter === t.k ? "border-b-2 border-[#0A0A0A] font-medium" : "text-[#666]"}`}>{t.l}</button>
        ))}
        <span className="ml-auto text-[10px] text-[#999] self-center">{total} Listings</span>
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="animate-spin text-[#666]" /></div>
      ) : (
        <div className="space-y-2">
          {items.map((l) => {
            const bodySet = new Set(l.body_content_urls || []);
            const wornSet = new Set(l.worn_photo_urls || []);
            return (
              <div key={l.listing_id} data-testid={`body-detection-listing-${l.listing_id}`} className="border border-[#E5E5E5] p-3">
                <div className="flex items-baseline justify-between mb-2">
                  <div>
                    <p className="text-sm font-semibold">{l.title || "(ohne Titel)"}</p>
                    <p className="text-[10px] text-[#999]">{l.public_item_no} · {(l.media_urls || []).length} media, {(l.worn_photo_urls || []).length} trage · {bodySet.size} als body getaggt</p>
                  </div>
                  <button data-testid={`body-detection-rescan-${l.listing_id}`} onClick={() => rescanOne(l.listing_id)}
                    className="h-7 px-2 rounded-full border border-[#0A0A0A] text-[#0A0A0A] text-[11px] flex items-center gap-1"><RefreshCw size={10} /> Rescan</button>
                </div>
                <div className="grid grid-cols-6 gap-1.5">
                  {(l.media_urls || []).map((url, i) => {
                    const isBody = bodySet.has(url);
                    return (
                      <div key={url + i} className={`relative aspect-square bg-[#F5F5F5] border ${isBody ? "border-red-400" : "border-emerald-400"}`}>
                        <img src={url} alt="" className={`w-full h-full object-cover ${isBody ? "blur-sm opacity-70" : ""}`} />
                        <button data-testid={`body-detection-toggle-${l.listing_id}-${i}`}
                          onClick={() => toggleUrl(l.listing_id, url, isBody)}
                          title={isBody ? "Als reines Artikelbild markieren" : "Als Body-Content markieren"}
                          className="absolute inset-x-0 bottom-0 h-6 bg-black/70 text-white text-[9px] flex items-center justify-center gap-1 hover:bg-black">
                          {isBody ? <><ShieldX size={10} /> BODY</> : <><ShieldCheck size={10} /> CLEAN</>}
                        </button>
                      </div>
                    );
                  })}
                  {(l.worn_photo_urls || []).map((url, i) => (
                    <div key={"w"+i} className="relative aspect-square bg-[#F5F5F5] border-2 border-emerald-600">
                      <img src={url} alt="" className="w-full h-full object-cover blur-sm opacity-70" />
                      <div className="absolute inset-x-0 bottom-0 h-6 bg-emerald-700 text-white text-[9px] flex items-center justify-center">TRAGE (fix)</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
          {items.length === 0 && <p className="text-xs text-[#999] italic text-center py-6">Keine Listings.</p>}
        </div>
      )}
    </div>
  );
}
